import { useEffect, useMemo, useState } from "react";
import api from "api/axiosInstance";
import styles from "../AdminPanel.module.scss";

type VideoRow = {
  id: string; // uuid (не показываем в UI)
  originalName: string;
  fileName: string;
  mimeType: string;
  size: number;
  path: string;
  cratedAt: string; // как в entity (опечатка)
};

type ClinicInfoRow = {
  id: number;
  name: string;
  detail: string;
};

// Ключи clinic-info для блока видео на главной
const KEY_HOME_VIDEO_TITLE = "home.videoTeaser.title";
const KEY_HOME_VIDEO_LEAD = "home.videoTeaser.lead";
const KEY_HOME_VIDEO_ID = "home.videoTeaser.videoId";

// Дефолты (как в текущем VideoTeaser.tsx)
const DEFAULT_HOME_TITLE = "Короткое видео о клинике";
const DEFAULT_HOME_LEAD =
  "Здесь будет видео‑визитка. Пока используем заглушку — позже заменим на реальный ролик.";

const ALLOWED_EXT = ["mp4", "webm", "mov", "mkv", "avi", "m4v", "ogv"];

function baseName(fileName: string) {
  const clean = (fileName || "").split("?")[0].split("#")[0];
  const dot = clean.lastIndexOf(".");
  return dot > 0 ? clean.slice(0, dot) : clean;
}

function getExt(name: string) {
  const clean = (name || "").toLowerCase();
  const dot = clean.lastIndexOf(".");
  return dot >= 0 ? clean.slice(dot + 1) : "";
}

function isAllowed(name: string) {
  const ext = getExt(name);
  return !!ext && ALLOWED_EXT.includes(ext);
}

function buildAccept() {
  return [
    "video/mp4",
    "video/webm",
    "video/quicktime",
    "video/x-matroska",
    "video/x-msvideo",
    "video/ogg",
    "video/x-m4v",
    ...ALLOWED_EXT.map((e) => `.${e}`),
  ].join(",");
}

function extractErr(e: any) {
  const status = e?.response?.status;
  const data = e?.response?.data;

  const msg =
    typeof data === "string"
      ? data
      : data?.message
      ? Array.isArray(data.message)
        ? data.message.join(", ")
        : String(data.message)
      : data?.error
      ? String(data.error)
      : e?.message;

  return status ? `${msg} (HTTP ${status})` : msg || "Неизвестная ошибка";
}

export default function VideosManager() {
  const acceptAttr = useMemo(() => buildAccept(), []);

  // =========================
  // Данные списка видео
  // =========================
  const [videos, setVideos] = useState<VideoRow[]>([]);

  // =========================
  // clinic-info (настройки видео на главной)
  // =========================
  const [clinicInfoRows, setClinicInfoRows] = useState<ClinicInfoRow[]>([]);
  const clinicByKey = useMemo(() => {
    const m = new Map<string, ClinicInfoRow>();
    clinicInfoRows.forEach((r) => m.set(r.name, r));
    return m;
  }, [clinicInfoRows]);

  const [homeTitle, setHomeTitle] = useState(DEFAULT_HOME_TITLE);
  const [homeLead, setHomeLead] = useState(DEFAULT_HOME_LEAD);
  const [homeVideoId, setHomeVideoId] = useState<string>("");

  // =========================
  // UI состояния
  // =========================
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  // =========================
  // Форма видео (низ страницы)
  // =========================
  const [editId, setEditId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const resetVideoForm = () => {
    setEditId(null);
    setTitle("");
    setFile(null);
  };

  // =========================
  // Загрузка данных (videos + clinic-info)
  // =========================
  const loadAll = async () => {
    setLoading(true);
    setErrorText(null);
    try {
      const [vRes, ciRes] = await Promise.all([
        api.get<VideoRow[]>("/admin/data/videos"),
        api.get<ClinicInfoRow[]>("/clinic-info"),
      ]);

      const v = (vRes.data ?? []).sort((a, b) => {
        const da = new Date(a.cratedAt ?? 0).getTime();
        const db = new Date(b.cratedAt ?? 0).getTime();
        return db - da;
      });

      setVideos(v);
      setClinicInfoRows(ciRes.data ?? []);
    } catch (e: any) {
      setErrorText(`Ошибка загрузки: ${extractErr(e)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  // Подтягиваем значения из clinic-info в форму "Видео на главной"
  useEffect(() => {
    const t = clinicByKey.get(KEY_HOME_VIDEO_TITLE)?.detail;
    const l = clinicByKey.get(KEY_HOME_VIDEO_LEAD)?.detail;
    const vid = clinicByKey.get(KEY_HOME_VIDEO_ID)?.detail;

    setHomeTitle(t || DEFAULT_HOME_TITLE);
    setHomeLead(l || DEFAULT_HOME_LEAD);
    setHomeVideoId(vid || "");
  }, [clinicByKey]);

  // =========================
  // clinic-info upsert (как в ContactsMapSettings)
  // =========================
  const upsertClinicInfo = async (name: string, detail: string) => {
    const existing = clinicByKey.get(name);
    if (existing) {
      await api.delete(`/clinic-info/${existing.id}`);
    }
    await api.post("/clinic-info", { name, detail });
  };

  const deleteClinicInfoKey = async (name: string) => {
    const existing = clinicByKey.get(name);
    if (existing) {
      await api.delete(`/clinic-info/${existing.id}`);
    }
  };

  const saveHomeVideoSettings = async () => {
    setLoading(true);
    setErrorText(null);
    try {
      await upsertClinicInfo(KEY_HOME_VIDEO_TITLE, (homeTitle || "").trim() || DEFAULT_HOME_TITLE);
      await upsertClinicInfo(KEY_HOME_VIDEO_LEAD, (homeLead || "").trim() || DEFAULT_HOME_LEAD);

      if (homeVideoId) {
        await upsertClinicInfo(KEY_HOME_VIDEO_ID, homeVideoId);
      } else {
        // если не выбрано — ключ удаляем, на главной будет заглушка
        await deleteClinicInfoKey(KEY_HOME_VIDEO_ID);
      }

      await loadAll();
    } catch (e: any) {
      setErrorText(`Ошибка сохранения настроек: ${extractErr(e)}`);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // CRUD видео
  // =========================
  const handleEdit = (row: VideoRow) => {
    setEditId(row.id);
    setTitle(row.originalName ?? "");
    setFile(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Удалить видео?")) return;

    setLoading(true);
    setErrorText(null);
    try {
      await api.delete(`/videos/${id}`);

      // если удалили то, что стояло на главной — сбросим выбор
      if (homeVideoId === id) {
        setHomeVideoId("");
        // ключ удалим при следующем сохранении или можно удалить сразу:
        await deleteClinicInfoKey(KEY_HOME_VIDEO_ID);
      }

      if (editId === id) resetVideoForm();
      await loadAll();
    } catch (e: any) {
      setErrorText(`Ошибка удаления: ${extractErr(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVideo = async () => {
    setLoading(true);
    setErrorText(null);

    try {
      const cleanTitle = (title || "").trim();

      // EDIT: только название
      if (editId) {
        if (!cleanTitle) {
          setErrorText("Укажите название");
          setLoading(false);
          return;
        }

        await api.patch(`/videos/${editId}`, { originalName: cleanTitle });
        resetVideoForm();
        await loadAll();
        return;
      }

      // CREATE: файл обязателен
      if (!file) {
        setErrorText("Выберите файл видео");
        setLoading(false);
        return;
      }

      if (!isAllowed(file.name)) {
        setErrorText(`Неподдерживаемый формат. Разрешено: ${ALLOWED_EXT.join(", ")}`);
        setLoading(false);
        return;
      }

      const finalTitle = cleanTitle || baseName(file.name);

      const fd = new FormData();
      fd.append("file", file);

      const created = await api.post<VideoRow>("/videos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // если пользователь поменял title — обновляем
      if (finalTitle && finalTitle !== created.data.originalName) {
        await api.patch(`/videos/${created.data.id}`, { originalName: finalTitle });
      }

      resetVideoForm();
      await loadAll();
    } catch (e: any) {
      setErrorText(`Ошибка сохранения: ${extractErr(e)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* =========================
          ВЕРХ: Видео на главной
         ========================= */}
      <div className={styles.form_block}>
        <h3>Видео на главной</h3>

        {errorText ? <div style={{ color: "#c00", marginBottom: 12 }}>{errorText}</div> : null}

        <label>Заголовок</label>
        <input value={homeTitle} onChange={(e) => setHomeTitle(e.target.value)} />

        <label>Текст</label>
        <textarea
          value={homeLead}
          onChange={(e) => setHomeLead(e.target.value)}
          rows={4}
          style={{ width: "60%" }}
        />

        <label>Выбрать видео</label>
        <select
          value={homeVideoId}
          onChange={(e) => setHomeVideoId(e.target.value)}
          style={{ width: "60%" }}
        >
          <option value="">(не выбрано — показывать заглушку)</option>
          {videos.map((v) => (
            <option key={v.id} value={v.id}>
              {v.originalName}
            </option>
          ))}
        </select>

        <button className={styles.btn_primary} onClick={saveHomeVideoSettings} disabled={loading}>
          {loading ? "Сохраняю..." : "Сохранить"}
        </button>
      </div>

      {/* =========================
          НИЗ: Управление видео
         ========================= */}
      <div className={styles.form_block}>
        <h3>{editId ? "Редактировать видео" : "Добавить видео"}</h3>

        {errorText ? <div style={{ color: "#c00", marginBottom: 12 }}>{errorText}</div> : null}

        <label>Название</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Автозаполнится из имени файла, можно изменить"
        />

        <label>Файл видео</label>
        <input
          type="file"
          accept={acceptAttr}
          disabled={Boolean(editId)}
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setFile(f);

            if (f) {
              const suggested = baseName(f.name);
              setTitle((prev) => (prev.trim() ? prev : suggested));
            }
          }}
        />

        <div style={{ marginTop: 8, opacity: 0.8, fontSize: 13 }}>
          Поддерживаемые форматы: {ALLOWED_EXT.join(", ")}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <button className={styles.btn_primary} onClick={handleSaveVideo} disabled={loading}>
            {loading ? "Сохраняю..." : editId ? "Сохранить" : "Создать"}
          </button>

          {editId ? (
            <button className={styles.btn} onClick={resetVideoForm} disabled={loading} type="button">
              Отмена
            </button>
          ) : null}
        </div>
      </div>

      <div className={styles.table_wrapper}>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Название</th>
              <th>Путь</th>
              <th>Действия</th>
            </tr>
          </thead>

          <tbody>
            {videos.map((r, idx) => (
              <tr key={r.id}>
                {/* ID — это нумерация с 1, UUID не показываем */}
                <td>{idx + 1}</td>
                <td>{r.originalName || "—"}</td>
                <td style={{ maxWidth: 560, wordBreak: "break-all" }}>{r.path || "—"}</td>
                <td>
                  <div className={styles.actions}>
                    <button className={styles.btn_primary} onClick={() => handleEdit(r)}>
                      Редактировать
                    </button>
                    <button className={styles.btn_danger} onClick={() => handleDelete(r.id)}>
                      Удалить
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {videos.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ opacity: 0.7 }}>
                  Пока нет записей
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}